import React, { useCallback, useEffect, useState } from 'react';

import { gql } from '@apollo/client';
import { Collapse, IconButton } from '@mui/material';
import clsx from 'clsx';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip } from 'src/components';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { getAreAllTasksDone } from 'src/components/workflow/board/workflow-card/task-progress';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { useDeletePhase } from 'src/hooks/phase';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { getWorkflowWasSentForSig } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

import { AddTaskInput } from './AddTaskInput';
import { sortTasksByCreation } from './phase-task-groups';
import { PhaseHomeColumnChip } from './PhaseHomeColumnChip';
import { PhaseStatusIndicator } from './PhaseStatusIndicator';
import { PhaseTaskRowWithSubtasks } from './PhaseTaskRowWithSubtasks';
import { nestSubtasks } from '../side-panel/task-review/task-review-helpers';
import type { ParkedWorkflowMeta } from '../use-parked-workflow-completion';
import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

interface PhaseCardStyleProps {
  isActive: boolean;
  isCompleted: boolean;
}

const useStyles = makeStyles<PhaseCardStyleProps>()((theme, { isActive, isCompleted }) => {
  const primaryColor = theme.palette.primary.main;

  return {
    content: {
      paddingBottom: theme.spacing(2),
    },
    divider: {
      borderTop: '1px solid rgba(217, 217, 217, 0.47)',
      margin: theme.spacing(1, 0),
    },
    header: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(2),
    },
    headerActions: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(0.5),
    },
    phaseName: {
      fontSize: 16,
      fontWeight: 'bold',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    phaseNameGroup: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      marginRight: 'auto',
      minWidth: 0,
    },
    phaseNameInput: {
      fontSize: 16,
      fontWeight: 'bold',
      padding: 0,
    },
    placeholderText: {
      color: 'rgba(55, 73, 81, 0.3)',
      fontSize: 16,
      lineHeight: '24px',
    },
    progressBar: {
      backgroundColor: 'rgba(217, 217, 217, 0.47)',
      borderRadius: 2,
      height: 3,
      overflow: 'hidden',
      width: '100%',
    },
    progressBarFill: {
      backgroundColor: isCompleted ? '#00866a' : isActive ? primaryColor : 'rgba(55, 73, 81, 0.2)',
      borderRadius: 2,
      height: '100%',
      transition: 'width 0.4s ease',
    },
    progressRow: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0, 2, 1.5),
    },
    progressText: {
      color: theme.appColors.iconsType,
      flexShrink: 0,
      fontSize: 12,
      whiteSpace: 'nowrap',
    },
    root: {
      backgroundColor: theme.palette.background.paper,
      border: isActive ? `2px solid ${primaryColor}` : '1px solid #e2e8f0',
      borderRadius: 12,
      overflow: 'hidden',
      width: '100%',
    },
    skeletonPhaseName: {
      color: theme.appColors.iconsType,
    },
    skeletonTaskRow: {
      backgroundColor: 'rgba(55, 73, 81, 0.06)',
      borderRadius: 6,
      height: 20,
      margin: '6px 8px',
    },
    tasksList: {
      display: 'flex',
      flexDirection: 'column',
    },
  };
});

interface PhaseCardProps {
  boardId: string;
  phase: ApolloTypes.PhaseSimple_NonSigningPhase_Fragment;
  workflow: ParkedWorkflowMeta;
  workflowColumnCategory: Apollo.ColumnCategory;
  canModifyStructure?: boolean;
  isActive?: boolean;
  readOnly?: boolean;
}

export const PhaseCard = React.memo(function PhaseCard({
  boardId,
  canModifyStructure,
  isActive = false,
  phase,
  readOnly = false,
  workflow,
  workflowColumnCategory,
}: PhaseCardProps) {
  const navigate = useNavigate();
  const { openDrawer } = useGlobalDrawer();
  const links = useWorkflowDetailLinks(workflow.id);
  const isCompleted = getAreAllTasksDone(phase.tasks);
  const canModify = !readOnly && (canModifyStructure ?? !isCompleted);
  const { classes } = useStyles({ isActive, isCompleted });
  const [expanded, setExpanded] = useState(!isCompleted);
  const handleToggle = useCallback(() => setExpanded(prev => !prev), []);

  const handleTaskCreated = useCallback(
    (task: ApolloTypes.TaskSimpleFragment) => {
      openDrawer();
      navigate(links.task(task.id));
    },
    [links, navigate, openDrawer]
  );

  /**
   * Collapse only at the moment the phase transitions to completed. Expansion
   * is otherwise purely user-controlled — auto-collapsing on mouse leave made
   * it impossible to interact with tasks inside a re-expanded completed phase.
   */
  useEffect(() => {
    if (isCompleted) setExpanded(false);
  }, [isCompleted]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.phaseNameGroup}>
          <PhaseNameEditor isCompleted={isCompleted} phase={phase} readOnly={readOnly} />
        </div>
        <div className={classes.headerActions} onClick={e => e.stopPropagation()}>
          {(isActive || !expanded) && (
            <PhaseStatusIndicator isActive={isActive} isCompleted={isCompleted} />
          )}
          <PhaseHomeColumnChip
            boardId={boardId}
            isPhaseActive={isActive}
            isPhaseCompleted={isCompleted}
            isSigningPhase={false}
            phase={phase}
            workflowColumnCategory={workflowColumnCategory}
          />
          {canModify && <DeletePhaseButton phase={phase} workflow={phase.workflow} />}
          <AlineTooltip arrow placement="top" title={expanded ? 'Collapse' : 'Expand'}>
            <IconButton size="small" onClick={handleToggle}>
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </IconButton>
          </AlineTooltip>
        </div>
      </div>
      <Collapse in={expanded}>
        <div className={classes.content}>
          <div className={classes.tasksList}>
            {nestSubtasks(sortTasksByCreation(phase.tasks)).map(row => (
              <PhaseTaskRowWithSubtasks
                key={row.task.id}
                phase={phase}
                readOnly={readOnly}
                row={row}
                workflow={workflow}
              />
            ))}
          </div>
          {!isCompleted && canModify && (
            <>
              <div className={classes.divider} />
              <AddTaskInput
                phaseId={phase.id}
                workflowId={workflow.id}
                onCreated={handleTaskCreated}
              />
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
});

export function PhaseCardSkeleton({
  aiIsGenerating = false,
  label = 'Loading phase...',
  taskCount = 2,
}: {
  aiIsGenerating?: boolean;
  label?: string;
  taskCount?: number;
}) {
  const { classes } = useStyles({ isActive: false, isCompleted: false });
  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.phaseNameGroup}>
          {aiIsGenerating && <PulseIcon variant={PulseIconVariant.LoadingPrimary} />}
          <span className={clsx(classes.phaseName, classes.skeletonPhaseName)}>{label}</span>
        </div>
      </div>
      <div className={classes.content}>
        <div className={classes.tasksList}>
          {Array.from({ length: taskCount }).map((_, i) => (
            <div key={i} className={classes.skeletonTaskRow} />
          ))}
        </div>
      </div>
    </div>
  );
}

const useEmptyPhaseCardStyles = makeStyles()(theme => ({
  addTaskRow: {
    alignItems: 'center',
    display: 'flex',
    padding: theme.spacing(0, 1),
  },
  content: {
    padding: theme.spacing(1),
  },
  header: {
    padding: theme.spacing(2),
  },
  phaseName: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  phaseNameGroup: {
    marginRight: 'auto',
  },
  placeholderText: {
    color: 'rgba(55, 73, 81, 0.3)',
    fontSize: 16,
  },
  root: {
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    width: '100%',
  },
  tasksList: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

/**
 * Non-interactive empty state card that matches PhaseCard styling. Used when there are no phases.
 */
export function EmptyPhaseCard() {
  const { classes } = useEmptyPhaseCardStyles();
  return (
    <div className={classes.root}>
      <div className={classes.header} style={{ cursor: 'default' }}>
        <div className={classes.phaseNameGroup}>
          <span className={clsx(classes.phaseName, classes.placeholderText)}>Add phase name</span>
        </div>
      </div>
      <div className={classes.content}>
        <div className={classes.tasksList} />
        <div className={classes.addTaskRow}>
          <span className={classes.placeholderText}>Add new task</span>
        </div>
      </div>
    </div>
  );
}

interface DeletePhaseButtonProps {
  phase: Pick<Apollo.Phase, 'id' | 'name'>;
  workflow: Pick<Apollo.Workflow, 'id' | 'state' | '__typename'>;
  isSigningPhase?: boolean;
}

export function DeletePhaseButton({
  isSigningPhase = false,
  phase,
  workflow,
}: DeletePhaseButtonProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const { deletePhase } = useDeletePhase();
  const handleConfirm = useCallback(async () => {
    await deletePhase(phase.id, workflow.id);
  }, [deletePhase, phase.id, workflow.id]);

  const wasSentForSignature = getWorkflowWasSentForSig(workflow);
  const message = isSigningPhase
    ? `Delete "${phase.name || 'Signing Phase'}"? All signing steps and field assignments will be removed, and signing documents will be moved to general.`
    : `Delete phase "${phase.name || 'Untitled Phase'}"? This will remove the phase and its tasks.`;

  if (isSigningPhase && wasSentForSignature) return null;

  return (
    <>
      <AlineTooltip arrow placement="top" title="Delete phase">
        <IconButton
          aria-label="Delete phase"
          size="small"
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          <Trash2 size={18} />
        </IconButton>
      </AlineTooltip>
      <ConfirmationDialog
        message={message}
        open={open}
        title="Delete Phase"
        onClickPrimary={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

void gql`
  mutation UpdatePhaseTitle($input: UpdatePhaseInput!) {
    updatePhase(input: $input) {
      phase {
        __typename
        id
        name
      }
    }
  }
`;

interface PhaseNameEditorProps {
  isCompleted: boolean;
  phase: ApolloTypes.PhaseSimple_NonSigningPhase_Fragment;
  readOnly: boolean;
}

function PhaseNameEditor({ isCompleted, phase, readOnly }: PhaseNameEditorProps) {
  const [updatePhaseTitle] = Apollo.useUpdatePhaseTitleMutation();
  const { classes } = useStyles({ isActive: false, isCompleted });

  const handleSave = useCallback(
    async (name: string) => {
      const trimmedName = name.trim();
      await updatePhaseTitle({
        optimisticResponse: {
          __typename: 'Mutation',
          updatePhase: {
            __typename: 'PhaseResponse',
            phase: {
              __typename: 'NonSigningPhase',
              id: phase.id,
              name: trimmedName,
            },
          },
        },
        variables: { input: { phaseId: phase.id, props: { name: trimmedName } } },
      });
    },
    [phase.id, updatePhaseTitle]
  );

  return (
    <InlineEditField
      classes={{
        display: classes.phaseName,
        input: classes.phaseNameInput,
        placeholder: classes.phaseName,
      }}
      disabled={isCompleted || readOnly}
      iconSize={16}
      placeholder="Type a phase name..."
      textPlaceholder="Untitled Phase"
      value={phase.name}
      onSave={handleSave}
    />
  );
}
